import { cn } from "@/lib/utils.ts";
import React from "react";
import ReactMarkdown from "react-markdown";
import { customMarkdownComponents, urnLinkTransform } from "./components.tsx";
import { MarkdownContext } from "./context.tsx";

const INLINE_ELEMENTS = ["strong", "em", "a", "code"];
const INLINE_ELEMENTS_WITHOUT_LINKS = ["strong", "em", "code"];

/** A paragraph as a block-level span, so the result stays valid inside a `<span>` or `<button>`. */
const BlockParagraph = ({ children }: { children?: React.ReactNode }) => (
  <span className="block">{children}</span>
);

/**
 * Render markdown in an inline context with limited support for bold, italic,
 * code, and links.
 *
 * `paragraphs` keeps paragraph breaks as stacked block spans instead of
 * unwrapping them into one run of text. For a multi-paragraph body shown in a
 * clamped space — a toast — the break is what separates a heading from the
 * item under it, and unwrapping would glue the two together.
 *
 * `links={false}` renders a link as its words alone. For a surface that is
 * itself one click target — a toast that opens its thread — a live link inside
 * it would answer the same click twice.
 */
export const InlineMarkdown = ({
  children,
  className,
  paragraphs = false,
  links = true,
  ...props
}: React.ComponentProps<typeof ReactMarkdown> & {
  className?: string;
  paragraphs?: boolean;
  links?: boolean;
}) => {
  const inlineElements = links ? INLINE_ELEMENTS : INLINE_ELEMENTS_WITHOUT_LINKS;
  // Minimal context for CustomMarkdownLink (no streaming, no message sending for
  // inline usage). Per-instance via useMemo so the Provider value keeps a stable identity
  const contextValue = React.useMemo(() => ({}), []);

  return (
    <MarkdownContext.Provider value={contextValue}>
      <span className={cn("inline", className)}>
        <ReactMarkdown
          // Only allow inline elements, plus paragraphs when asked for
          allowedElements={paragraphs ? [...inlineElements, "p", "br"] : inlineElements}
          unwrapDisallowed={true}
          // Use the same link component and URL transform as main Markdown
          urlTransform={urnLinkTransform}
          components={{
            // Use the same link handling logic as the main markdown components
            a: customMarkdownComponents.a,
            // Let strong and em use ReactMarkdown defaults
            ...(paragraphs ? { p: BlockParagraph } : {}),
          }}
          {...props}
        >
          {children}
        </ReactMarkdown>
      </span>
    </MarkdownContext.Provider>
  );
};
